import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const step1Schema = z.object({
  first_name: z.string()
    .min(2, "First name must be at least 2 characters")
    .max(50, "First name must be less than 50 characters")
    .regex(/^[A-Za-z\s\-']+$/, "First name can only contain letters, spaces, hyphens, and apostrophes")
    .refine(
      (val) => !/\d/.test(val),
      "First name cannot contain numbers. Please check for typos."
    ),
  last_name: z.string()
    .min(2, "Last name must be at least 2 characters")
    .max(50, "Last name must be less than 50 characters")
    .regex(/^[A-Za-z\s\-']+$/, "Last name can only contain letters, spaces, hyphens, and apostrophes")
    .refine(
      (val) => !/\d/.test(val),
      "Last name cannot contain numbers. Please check for typos."
    ),
  business_name: z.string().max(150).optional().or(z.literal('')),
  email: z.string().email("Please enter a valid email address").max(150),
  phone: z.string()
    .min(10, "Phone number must be at least 10 digits")
    .max(20, "Phone number is too long")
    .regex(/^[\d\s\-\(\)\+]+$/, "Phone number can only contain digits, spaces, hyphens, parentheses, and plus sign")
    .refine(
      (val) => {
        const digitsOnly = val.replace(/\D/g, '');
        return digitsOnly.length >= 10 && digitsOnly.length <= 15;
      },
      "Phone number must contain 10-15 digits"
    )
});

const LeadFormStep1 = ({ onComplete }) => {
  const [showBusinessName, setShowBusinessName] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(step1Schema)
  });
  
  const onSubmit = async (data) => {
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/leads/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(result.message);
        onComplete({
          ...data,
          lead_id: result.lead_id,
          verification_id: result.verification_id,
          email: data.email
        });
      } else {
        const errorMsg = result.error?.message || result.detail?.error?.message || 'Submission failed';
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error('Network error. Please check your connection and try again.');
      console.error('Step 1 error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-navy py-16 px-4">
      <div className="max-w-2xl mx-auto">
        <Card className="p-8 bg-ivory">
          <CardHeader>
            <CardTitle className="text-3xl font-playfair text-navy mb-2">
              Let's Get Started
            </CardTitle>
            <p className="text-gray-600">
              Tell us a bit about yourself so we can better assist you.
            </p>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Progress indicator */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-gold text-white flex items-center justify-center font-semibold">1</div>
                <span className="ml-2 text-sm font-medium">Basic Info</span>
              </div>
              <div className="flex-1 h-0.5 bg-gray-300 mx-4" />
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-gray-300 text-gray-600 flex items-center justify-center">2</div>
                <span className="ml-2 text-sm text-gray-500">Verify Email</span>
              </div>
              <div className="flex-1 h-0.5 bg-gray-300 mx-4" />
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-gray-300 text-gray-600 flex items-center justify-center">3</div>
                <span className="ml-2 text-sm text-gray-500">Details</span>
              </div>
            </div>
            
            <form data-testid="lead-form-step1-form-1" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    {...register('first_name')}
                    className="focus:ring-gold focus:border-gold"
                    data-testid="first-name-input"
                  />
                  {errors.first_name && (
                    <p className="text-red-500 text-sm mt-1">{errors.first_name.message}</p>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    {...register('last_name')}
                    className="focus:ring-gold focus:border-gold"
                    data-testid="last-name-input"
                  />
                  {errors.last_name && (
                    <p className="text-red-500 text-sm mt-1">{errors.last_name.message}</p>
                  )}
                </div>
              </div>
              
              <div>
                <Label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox data-testid="lead-form-step1-checkbox-1"
                    checked={showBusinessName}
                    onCheckedChange={setShowBusinessName}
                  />
                  I'm inquiring on behalf of a business
                </Label>
              </div>
              
              {showBusinessName && (
                <div>
                  <Label htmlFor="business_name">Business Name</Label>
                  <Input
                    id="business_name"
                    {...register('business_name')}
                    className="focus:ring-gold focus:border-gold"
                    data-testid="business-name-input"
                  />
                  {errors.business_name && (
                    <p className="text-red-500 text-sm mt-1">{errors.business_name.message}</p>
                  )}
                </div>
              )}
              
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email')}
                  className="focus:ring-gold focus:border-gold"
                  data-testid="email-input"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  type="tel"
                  maxLength={20}
                  {...register('phone')}
                  placeholder="Enter your phone number"
                  className="focus:ring-gold focus:border-gold"
                  data-testid="phone-input"
                />
                <p className="text-gray-500 text-sm mt-1">US format preferred</p>
                {errors.phone && (
                  <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
                )}
              </div>
              
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-gold to-gold-600 text-white hover:from-gold-600 hover:to-gold-700"
                disabled={isSubmitting}
                data-testid="continue-button"
              >
                {isSubmitting ? 'Processing...' : 'Continue'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LeadFormStep1;
